import fs from 'fs';
import path from 'path';
import fg from 'fast-glob';
import { execSync } from 'child_process';
import chalk from 'chalk';

import { resolve } from '@wikibonsai/almanac';
import { MD } from './const';
import { getDocTypes } from './config';


// files — memoized so commands that glob multiple times
// (getFileNames, getFileUris, getIndexFileUris) only walk
// the filesystem once per invocation.

let _gardenFileUris: string[] | undefined;
let _gardenCwd: string | undefined;
let _gardenMtimeMs: number | undefined;
export const INDEX_GLOB: string = './index/**/*';

function cwdMtimeMs(): number {
  try { return fs.statSync(process.cwd()).mtimeMs; }
  catch { return -1; }
}

export function resetGlobCache(): void {
  _gardenFileUris = undefined;
  _gardenCwd = undefined;
  _gardenMtimeMs = undefined;
}

export function getFileUris(): string[] {
  const cwd: string = process.cwd();
  const mtimeMs: number = cwdMtimeMs();
  if (!_gardenFileUris || _gardenCwd !== cwd || _gardenMtimeMs !== mtimeMs) {
    _gardenCwd = cwd;
    _gardenMtimeMs = mtimeMs;
    _gardenFileUris = fg.sync(cwd + '/**/*' + MD);
  }
  return _gardenFileUris;
}

export function getFileNames(): string[] {
  return getFileUris().map((fp) => path.basename(fp, MD));
}

export function getIndexFileUris(doctypePath: string, indexGlob: string | undefined): string[] | undefined {
  let fileUris: string[] = [];
  // ...from glob
  if (indexGlob !== undefined) {
    try {
      fileUris = fg.sync(path.join(process.cwd(), indexGlob + MD));
      if (fileUris.length === 0) {
        console.warn(chalk.yellow('no index files found at location: ' + '"' + indexGlob + '"'));
        return;
      }
    } catch (err) {
      console.error(err);
      return [];
    }
  }
  // ...from doctype
  const doctypes: any = getDocTypes(doctypePath);
  if (doctypes.types?.index) {
    const cwd: string = process.cwd();
    const gardenFileUris: string[] = getFileUris();
    gardenFileUris.forEach((uri: string) => {
      const fname: string = path.basename(uri, MD);
      const dtype: string = resolve(doctypes.types, fname, uri, cwd);
      if (dtype === 'index') {
        fileUris.push(uri);
      }
    });
    if (fileUris.length === 0) {
      console.error('no index files found from doctype payload: ' + JSON.stringify(doctypes));
      return;
    }
  }
  // check base default (e.g. './index/')
  // if no 'index' doctype provided
  if (fileUris.length === 0) {
    fileUris = fg.sync(path.join(process.cwd(), INDEX_GLOB + MD));
  }
  return fileUris;
}

export function pathHasDir(p: string): boolean {
  return path.normalize(p).includes(path.sep);
}

export function resolveSourcePath(fromArg: string, cwd: string): string | undefined {
  if (pathHasDir(fromArg)) {
    const p: string = path.resolve(cwd, fromArg);
    if (fs.existsSync(p) && p.endsWith(MD)) {
      return p;
    }
    if (fs.existsSync(p + MD)) {
      return p + MD;
    }
    return undefined;
  }
  const base: string = path.basename(fromArg, MD);
  for (const fp of getFileUris()) {
    if (path.basename(fp, MD) === base) {
      return fp;
    }
  }
  return undefined;
}

export function resolveDestPath(toArg: string, cwd: string, sourcePath: string): string {
  if (pathHasDir(toArg)) {
    const resolved: string = path.resolve(cwd, toArg);
    return resolved.endsWith(MD) ? resolved : resolved + MD;
  }
  const newBasename: string = path.basename(toArg, MD);
  return path.join(path.dirname(sourcePath), newBasename + MD);
}

// regex

export function hasOneCapGrp(regexStr: string): boolean {
  /* eslint-disable indent */
  // Convert the regex to a string and remove escaped parentheses and non-capturing groups
  const regexString = regexStr.replace(/\\[()]/g, '')        // Remove escaped parentheses
                              .replace(/\(\?:[^)]*\)/g, ''); // Remove non-capturing groups
  /* eslint-enable indent */
  const hasLeftRightParen: RegExpMatchArray | null = regexString.match(/\([^)]*\)/g);
  return hasLeftRightParen !== null && hasLeftRightParen.length === 1;
}

export function isValidRegex(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch (e) {
    return false;
  }
}

// parse

// todo: replace with `caml.update()`
export const update = (key: string, value: string, content: string): string => {
  // CAML: optional leading colons / whitespace before the key; then `::` and value.
  // Matches e.g. `title :: ...`, `: title :: ...`, `:: title :: ...`
  const camlRe = new RegExp(`^((?:\\s*:+\\s*)?${key}\\s*::\\s*)(.*)$`, 'm');
  let next = content.replace(camlRe, (_match, prefix: string) => `${prefix}${value}`);

  // YAML: only update keys inside leading `---` … `---` frontmatter (not body / code blocks).
  const openFm = next.match(/^\uFEFF?---\s*\r?\n/);
  if (!openFm) {
    return next;
  }
  const headLen = openFm[0].length;
  const afterOpen = next.slice(headLen);
  const closeFm = /\r?\n---\s*(?:\r?\n|$)/;
  const cm = closeFm.exec(afterOpen);
  if (!cm) {
    return next;
  }
  const fmBody = afterOpen.slice(0, cm.index);
  const fromClose = afterOpen.slice(cm.index);
  const yamlRe = new RegExp(`^(${key}\\s*:\\s*)(.*)$`, 'gm');
  const newFmBody = fmBody.replace(yamlRe, (_match, prefix: string) => `${prefix}${value}`);
  if (newFmBody !== fmBody) {
    next = next.slice(0, headLen) + newFmBody + fromClose;
  }

  return next;
};

// post-rename operations

import * as wikirefs from 'wikirefs';
import { filenameToTitle, parseRenameOpts } from './case';
import type { RenameOpts } from './case';

/**
 * Update the title attribute in a file after rename.
 * Returns true if the title was changed.
 */
export function updateTitle(filePath: string, newBasename: string, opts?: any): boolean {
  const renameOpts: RenameOpts = parseRenameOpts(opts);
  if (renameOpts.noTitle) { return false; }
  const newTitle: string = renameOpts.explicitTitle !== undefined
    ? renameOpts.explicitTitle
    : filenameToTitle(newBasename, renameOpts.caseKind);
  try {
    const content: string = fs.readFileSync(filePath, 'utf8');
    const updated: string = update(renameOpts.titleKeyRe, newTitle, content);
    if (content !== updated) {
      fs.writeFileSync(filePath, updated, 'utf8');
      return true;
    }
  } catch (_e: any) {
    // best-effort
  }
  return false;
}

/**
 * Sync wikilinks across the vault after a basename change.
 * Returns list of changed basenames and errors.
 */
export function syncWikilinks(
  oldBasename: string,
  newBasename: string,
): { changed: string[]; errors: string[] } {
  const changed: string[] = [];
  const errors: string[] = [];
  if (oldBasename === newBasename) { return { changed, errors }; }
  const vaultPaths: string[] = getFileUris();
  for (const fp of vaultPaths) {
    try {
      const oldContent: string = fs.readFileSync(fp, 'utf8');
      const newContent: string = wikirefs.renameFileName(oldBasename, newBasename, oldContent);
      if (oldContent !== newContent) {
        fs.writeFileSync(fp, newContent, 'utf8');
        changed.push(path.basename(fp, MD));
      }
    } catch (e: any) {
      errors.push(String(e));
    }
  }
  return { changed, errors };
}

/**
 * Batch sync wikilinks for multiple renames in one pass.
 */
export function syncWikilinksBatch(
  renames: [string, string][],
): { changed: string[]; errors: string[] } {
  const changed: string[] = [];
  const errors: string[] = [];
  if (renames.length === 0) { return { changed, errors }; }
  const vaultPaths: string[] = getFileUris();
  for (const fp of vaultPaths) {
    try {
      const oldContent: string = fs.readFileSync(fp, 'utf8');
      let newContent: string = oldContent;
      for (const [oldBase, newBase] of renames) {
        newContent = wikirefs.renameFileName(oldBase, newBase, newContent);
      }
      if (oldContent !== newContent) {
        fs.writeFileSync(fp, newContent, 'utf8');
        changed.push(path.basename(fp, MD));
      }
    } catch (e: any) {
      errors.push(String(e));
    }
  }
  return { changed, errors };
}

// cli

export function ls(path: string): string | undefined {
  try {
    return execSync(`ls ${path}`).toString();
  } catch (e) {
    console.error(`error executing 'ls': ${e}`);
  }
}
